import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ManagerCourse } from "../../interfaces/manager.application";
import { getQueryString } from "../../utils/helper";
import { getToken } from "../../utils/tokenStore";

const initialState = {
  loading: false,
  managerCourses: [] as ManagerCourse[],
  selectedCourseApplication: {} as ManagerCourse,
  updateLoading: false,
};

export const getManagerApplications = createAsyncThunk(
  "managerApplications/getManagerApplications",
  async (data: { status: string }, { getState, rejectWithValue }) => {
    try {
      const { status } = data;
      const query = getQueryString({
        user_id: sessionStorage.getItem("userId"),
        role: "manager",
        status: status,
      });

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const getManagerApplicationsApplyForTeam = createAsyncThunk(
  "managerApplications/getManagerApplicationsApplyForTeam",
  async (
    data: { status: string; course_id?: string | number },
    { getState, rejectWithValue }
  ) => {
    try {
      const { status } = data;
      // Construct the query string with user_id, role, and status
      // If course_id is provided, include it in the query
      // Otherwise, just use user_id and role
      const queryParams: any = {
        user_id: sessionStorage.getItem("userId"),
        role: "manager",
      };
      if (data.course_id) {
        queryParams.course_id = data.course_id;
      }
      if (status) {
        queryParams.status = status;
      }
      const query = getQueryString(queryParams);

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const updateManagerApplicationStatus = createAsyncThunk(
  "managerApplications/updateManagerApplicationStatus",
  async (
    props: {
      status: string;
      courseId: string | number;
      userId: string | number;
      travel?: boolean | null;
      manager_id?: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const payload: any = {
        user_id: props.userId,
        role: "manager",
        course_id: props.courseId,
      };
      if (props.manager_id) {
        payload.manager_id = props.manager_id;
      }
      const query = getQueryString(payload);

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications/${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "PUT",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          application_status: props.status,
          travel: props.travel,
        }),
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const managerApplicationsSlice = createSlice({
  name: "managerApplications",
  initialState: initialState,
  reducers: {
    setSelectedCourseApplication: (state, action) => {
      state.selectedCourseApplication = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getManagerApplications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getManagerApplications.fulfilled, (state, action) => {
        state.loading = false;
        // Initialize an empty array to hold transformed courses
        const transformedCourses: any = [];

        // Loop through each course in the action payload data
        action.payload.data.forEach((course: any) => {
          course.applications.forEach((application: any) => {
            transformedCourses.push({
              ...course,
              applications: [{ ...application }],
            });
          });
        });

        // Assign the transformed data to state
        state.managerCourses = transformedCourses;
      })
      .addCase(getManagerApplications.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getManagerApplicationsApplyForTeam.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getManagerApplicationsApplyForTeam.fulfilled,
        (state, action) => {
          state.loading = false;

          state.managerCourses = action.payload.data;
        }
      )
      .addCase(getManagerApplicationsApplyForTeam.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(updateManagerApplicationStatus.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateManagerApplicationStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
      })
      .addCase(updateManagerApplicationStatus.rejected, (state) => {
        state.updateLoading = false;
      });
  },
});

export default managerApplicationsSlice.reducer;

export const { setSelectedCourseApplication } =
  managerApplicationsSlice.actions;
